import {z} from 'zod';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';
import {
  recommendationRules,
  recommendationStatuses,
} from '@packages/domains/recommendations/data-types';

const toolName = 'get-site-recommendations' as const;

const argumentZod = z.object({
  siteId: z.string().describe('The site ID to get recommendations for'),
  rule: z
    .enum([...recommendationRules])
    .optional()
    .describe(
      'Filter by recommendation rule (e.g. broken_link, page_title_required)'
    ),
  status: z
    .enum([...recommendationStatuses])
    .optional()
    .describe(
      'Filter by recommendation status (e.g. active, applied, dismissed). Defaults to all statuses if not specified.'
    ),
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .describe(
      'Maximum number of recommendations to return (1-100, default 10)'
    ),
  offset: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe(
      'Number of items to skip for pagination. Use with hasMore to fetch subsequent pages.'
    ),
});

export const getSiteRecommendationsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Get site recommendations',
  family: 'recommendations',
  llmTool: {
    name: toolName,
    description:
      'Gets a paginated list of site recommendations. Optionally filter by rule and status. Returns recommendations with pagination metadata.',
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [
    {
      resource: 'SiteAeoRecommendations',
      action: 'read',
      resolveResources: (args) => [
        {type: 'SiteAeoRecommendations', id: args.siteId},
      ],
    },
  ],
  confirmBeforeExecuting: false,
});

export type GetSiteRecommendationsArgumentType = z.infer<typeof argumentZod>;
